import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  CalendarCheck,
  CreditCard,
  KeyRound,
  Home as HomeIcon,
  ShieldCheck,
  LogOut,
  type LucideIcon,
} from "lucide-react";
import { Layout } from "@/frontend/components/layout/Layout";
import { Input } from "@/frontend/components/ui/input";
import { Button } from "@/frontend/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/frontend/components/ui/accordion";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/frontend/components/ui/breadcrumb";

// A paragraph (string), a bulleted list, or an image.
// width/height are the file's intrinsic pixel size -- they reserve the right
// space before it loads, so the answer doesn't jump as the image arrives.
type FaqImage = {
  src: string;
  alt: string;
  width: number;
  height: number;
  caption?: string;
};
type FaqBlock = string | { list: string[] } | { image: FaqImage };
interface FaqItem {
  q: string;
  a: FaqBlock[];
}
interface FaqCategory {
  id: string;
  title: string;
  icon: LucideIcon;
  items: FaqItem[];
}

const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: "booking",
    title: "Booking",
    icon: CalendarCheck,
    items: [
      {
        q: "How do I book a room at Home 2 Students?",
        a: [
          "Booking a room at H2S is super easy! Just use the booking bar at the top or bottom of our website:",
          {
            list: [
              "Select the residence or the period you are looking for",
              "You'll then see all the available residences and room types for that period",
              "Choose your preferred property and room",
              "Fill in your contact details",
            ],
          },
          "And that's it! Your reservation request is on its way.",
        ],
      },
      {
        q: "Who can live in H2S student housing?",
        a: [
          "Students from all backgrounds are welcome. If you are 18 years or older and enrolled in a full-time or part-time study programme, you can make a reservation and join our student community at Home 2 Students.",
        ],
      },
      {
        q: "Do I need to be a full-time student to book a room?",
        a: [
          "No, you don't have to be a full-time student to live with us. Both full-time and part-time students aged 18 or older are welcome at H2S. As long as you are enrolled in an accredited study programme (and you have proof of enrollment), you can book a room and become part of our student community.",
        ],
      },
      {
        q: "Can international students book a room?",
        a: [
          "Yes, absolutely! International students are more than welcome at H2S.",
          "Home 2 Students offers a truly international environment where you can meet new people, make friends from all over the world, and feel at home from day one.",
        ],
      },
      {
        q: "Can I choose my exact room or studio?",
        a: [
          "At the João Crisóstomo residence, you'll be able to choose your preferred room category during booking, and we will assign you a specific room prior to your arrival. While we cannot guarantee a particular room, we always do our best to accommodate any preferences you share with us.",
          "At the Miguel Bombarda and Sá da Bandeira apartments, you choose the exact room.",
        ],
      },
      {
        q: "How long are Home 2 Students tenancy agreements?",
        a: [
          "At Home 2 Students, the standard contract duration is a minimum of 10 months, covering the full academic year. For students enrolled in summer courses or internships, shorter-term contracts are also available for the summer period.",
        ],
      },
      {
        q: "How far in advance can I book accommodation?",
        a: [
          "We open bookings on a rolling basis, and as soon as new dates become available you'll see them listed on our website. We recommend checking back regularly so you don't miss newly released availability.",
        ],
      },
      {
        q: "What documents do I need to complete my booking?",
        a: [
          "You'll need to provide the following:",
          {
            list: [
              "A copy of your passport, or a local ID card if you are an EU national",
              "A university acceptance letter, or a copy of your student card if you're already enrolled",
            ],
          },
        ],
      },
      {
        q: "What happens after I complete my booking?",
        a: [
          "After completing your booking and payment, you will receive a confirmation email. Your contract, payment schedule, terms and conditions, cancellation policy, and general rules will be available online through our student portal.",
        ],
      },
      {
        q: "Do I need a guarantor?",
        a: ["No, you will not need a guarantor."],
      },
    ],
  },
  {
    id: "payments-contracts",
    title: "Payments & Contracts",
    icon: CreditCard,
    items: [
      {
        q: "Do I have to sign a contract?",
        a: [
          "Yes. According to Portuguese law, every person renting a room must have a valid contract for the duration of their stay.",
        ],
      },
      {
        q: "What is included in the first payment?",
        a: [
          "To confirm your booking, you'll need to pay the first month's rent in advance.",
          "The administration fee, the checkout cleaning fee, and the refundable deposit are paid when you check in, receive the keys, and sign the contract.",
        ],
      },
      {
        q: "When is my rent due each month?",
        a: [
          "Rent is paid monthly, no later than the 1st of each month. If this date is not suitable for you, please contact our team so we can assist you.",
        ],
      },
      {
        q: "What payment methods are accepted?",
        a: [
          "You can pay through bank wire transfer or cash. Debit and credit cards are not accepted.",
        ],
      },
      {
        q: "Can I change my payment method after booking?",
        a: [
          "Yes, you can update your preferred payment method whenever you need. Once the payment for a specific month has been processed, the change will apply starting from the next payment.",
        ],
      },
      {
        q: "Can I pay my rent in advance?",
        a: [
          "Yes, you can pay your entire contract upfront if you wish. Please contact our team so we can confirm the conditions for you.",
        ],
      },
      {
        q: "What happens if I pay my rent late?",
        a: [
          "It's your responsibility to keep your payments up to date. If the monthly payment is not received within 8 days after the due date stated in the instalment calendar, a 10% late fee will be applied to the outstanding amount. If the delay reaches 15 days, Home 2 Students will consider the accommodation contract terminated. In this case, no refunds will be issued for any amounts already paid, including the last month's instalment, fees, or the deposit.",
        ],
      },
      {
        q: "Will my rent increase during my contract?",
        a: [
          "No. The rental fee will not change during your active contract under any circumstances.",
        ],
      },
      {
        q: "What is included in the rent?",
        a: [
          "The rent includes all expenses for water, gas, and internet access, as well as common-area cleaning and electricity (up to a monthly limit of 35 kWh per person). It is a large limit that will provide more than enough comfort for your stay (as long as you don't forget to turn off your lights or heater, you should be fine!).",
          "Every room comes furnished for both living and studying. It also includes access to all common areas and the use of all equipment available in those spaces.",
        ],
      },
      {
        q: "Is a deposit required? How much is it?",
        a: [
          "Yes. A refundable deposit equivalent to two (2) months' instalment is required. This deposit is intended to cover any charges that may arise from your stay beyond normal wear and tear.",
        ],
      },
      {
        q: "Are there any booking or administration fees?",
        a: [
          "Yes. There is a tax payment of 10% of your monthly rent, to be paid at check-in. It's a one-time payment, so if your rent is 500€ you will pay 50€, for example.",
        ],
      },
      {
        q: "What is the cancellation policy?",
        a: [
          "To cancel your booking, please send an email to geral@home2students.com explaining the reason for your cancellation. The refund conditions are as follows:",
          {
            list: [
              "60 days before the start of the rental period: the full refundable deposit will be returned.",
              "30 days before the start of the rental period: 50% of the refundable deposit will be returned.",
              "Less than 30 days before the start of the rental period: the refundable deposit is not returned, except if the student is not admitted to a university in the same city. In that case, a full refund will be issued upon presentation of valid supporting documents.",
            ],
          },
        ],
      },
      {
        q: "Can I terminate my contract early?",
        a: [
          "No, early termination is not possible. You are required to stay for the full duration stated in your rental agreement.",
        ],
      },
    ],
  },
  {
    id: "moving-in",
    title: "Moving In",
    icon: KeyRound,
    items: [
      {
        q: "When can I move in?",
        a: [
          "You may move in any time during office hours, starting from the official start date of your contract. If you expect to arrive outside office hours, please contact the local team in advance to request approval.",
          "You will also receive detailed arrival instructions approximately a few weeks before your tenancy begins.",
          "Office hours are from 10h until 18h, Monday to Friday. Moving in outside this schedule has a cost of 30€ until 23h, and 50€ between 23h and 9h.",
        ],
      },
      {
        q: "Can I move in earlier than my contract start date?",
        a: [
          "In general, moving in before your official contract start date is not possible. Your room will only be available from the date stated in your lease agreement.",
          "If you have a special circumstance and need to request an earlier arrival, please contact your local team. They can check availability and let you know whether an early move-in can be accommodated, though this is not guaranteed.",
        ],
      },
      {
        q: "How will I receive my keys or building access?",
        a: [
          "All new tenants will receive detailed move-in and check-in instructions a few weeks before the start of their lease agreement. This information will explain how to collect your keys or access credentials, as well as any specific procedures for your building.",
        ],
      },
      {
        q: "Will there be staff available when I arrive?",
        a: [
          "Yes. You will need to share all your travel details so that someone from our staff can be waiting for you at the residence when you arrive.",
        ],
      },
      {
        q: "What documents do I need to check in?",
        a: [
          "To check in you will need a copy of your passport, your VISA (if you are not an EU resident), and proof of university enrollment (or internship).",
        ],
      },
      {
        q: "Is my room furnished?",
        a: ["Yes, all rooms are fully furnished."],
      },
      {
        q: "Is there an inventory or room inspection at move-in?",
        a: [
          "Yes, we carry out a room inspection and inventory at move-in. This helps ensure everything is in good condition when you arrive and gives you peace of mind throughout your stay.",
        ],
      },
      {
        q: "When do I need to pay my deposit?",
        a: ["At check-in. The deposit is the same amount as two (2) months' rent."],
      },
      {
        q: "What does the Kitchen Pack include?",
        a: [
          {
            list: [
              "3-piece plate set (main, soup, dessert)",
              "1 Mug",
              "1 Water glass",
              "3 Pans",
              "1 Frying pan",
              "1 Cutlery set (fork, knife, tablespoon, small spoon)",
            ],
          },
        ],
      },
      {
        q: "What does the Linens and Towel Pack include?",
        a: [
          {
            image: {
              src: "/bed-and-bath-kit-price-list-2026-2027.png",
              width: 1404,
              height: 2391,
              // Text alternative for a table delivered as a raster image.
              alt: "Home2Students price list for 2026/2027, in Portuguese and English. Single bed (90x200): Complete Bed Kit 69,83€, Partial Bed Kit 42,19€, Comfort Bed Kit 27,64€. Double bed (140/160x200): Complete Bed Kit 93,85€, Partial Bed Kit 62,68€, Comfort Bed Kit 31,17€. Bath Kit 14,98€. Each kit lists its contents, and every price is shown before and after tax.",
              caption: "Bed linen and towel kits — price list 2026/2027.",
            },
          },
        ],
      },
    ],
  },
  {
    id: "living",
    title: "Living at Home 2 Students",
    icon: HomeIcon,
    items: [
      {
        q: "Are utilities included in my rent?",
        a: [
          "Yes, the following utilities are included:",
          {
            list: [
              "Water",
              "Electricity (up to a monthly limit of 35 kWh per person)",
              "Gas",
              "Common-area cleaning",
              "Wi-Fi",
            ],
          },
        ],
      },
      {
        q: "Is internet included?",
        a: [
          "Yes. Every private and common area in our student residences has super-fast Wi-Fi.",
        ],
      },
      {
        q: "Can I change rooms after moving in?",
        a: [
          "Please contact the local team to check if this change is possible. We cannot guarantee availability, and a fee may apply.",
        ],
      },
      {
        q: "Am I allowed to have guests?",
        a: [
          "You are allowed to invite one guest throughout the day. You are also allowed limited overnight guests; however, you will need to inform the H2S team via email beforehand and ask for authorization. More than 2 sleepovers per month may incur extra costs.",
        ],
      },
      {
        q: "How can I receive my mail?",
        a: [
          "The postman will leave all mail at the residence entrance. When you arrive, just check if something has been delivered for you.",
          "For package delivery, you need to agree on a time and share your contact details so that the courier delivers directly to you.",
        ],
      },
    ],
  },
  {
    id: "security-maintenance",
    title: "Security & Maintenance",
    icon: ShieldCheck,
    items: [
      {
        q: "Is there staff on-site?",
        a: ["You can easily reach any of our staff members using WhatsApp."],
      },
      {
        q: "Is there CCTV in the building?",
        a: ["Only in the common areas of the João Crisóstomo residence."],
      },
      {
        q: "What should I do if I lose my key or access card?",
        a: [
          "Please contact the local team as soon as possible so they can sort this out.",
        ],
      },
      {
        q: "What should I do if something breaks in my room?",
        a: [
          "If something breaks in your room, please report it to our team. We will assess the issue and arrange the necessary maintenance or replacement. Please note that if the damage was caused by misuse or is not considered normal wear and tear, repair costs may apply.",
        ],
      },
      {
        q: "How quickly are maintenance issues resolved?",
        a: [
          "Maintenance requests are usually handled very quickly. Once you submit one, our team reviews it as soon as possible and prioritizes it based on urgency. Most issues are resolved within a short timeframe, and urgent cases are addressed right away.",
        ],
      },
    ],
  },
  {
    id: "moving-out",
    title: "Moving Out",
    icon: LogOut,
    items: [
      {
        q: "When do I need to move out?",
        a: [
          "You will need to move out on or before the last day of your contract, before 12h. Please make sure all your belongings are removed and the room is left clean and in good condition by that date.",
        ],
      },
      {
        q: "What is the move-out process?",
        a: [
          "The move-out process is simple. A room inspection will be carried out before you leave, and any refundable amounts, like your deposit, will be processed based on the condition of the room, furniture, and equipment. If you have any questions or need help preparing, our local team is always happy to assist.",
        ],
      },
      {
        q: "Do I need to schedule a room inspection before leaving?",
        a: [
          "Yes, a room inspection must be scheduled before you leave. Please contact the local team in advance to arrange a suitable time for the inspection.",
        ],
      },
      {
        q: "Do I need to clean my room before leaving?",
        a: [
          "Before you leave, make sure all your belongings are removed and the space is left clean and in good condition.",
        ],
      },
      {
        q: "How do I return my keys or access card?",
        a: [
          "Please return it to the local team when you move out. A fee may apply if it's not returned or if it's damaged.",
        ],
      },
      {
        q: "Can I extend my stay beyond my contract?",
        a: [
          "Any contract extension needs to be approved by H2S before the original contract termination.",
          "You always need to leave your room no later than the last day of your contract, before 12h, making sure all your belongings are removed and the space is left clean and in good condition.",
        ],
      },
      {
        q: "Can I move out earlier than my contract end date?",
        a: [
          "Yes, you can move out earlier if you need to, but you remain responsible for paying the rent until the official end date of your contract. Your rental agreement is fixed for the full duration, so moving out does not shorten or cancel the financial commitment.",
          "If your situation changes, feel free to contact the residence team. They can advise you on available options or next steps, but the contractual rent obligation remains in place.",
        ],
      },
    ],
  },
];

const blockText = (blocks: FaqBlock[]) =>
  blocks
    .map((b) => {
      if (typeof b === "string") return b;
      if ("list" in b) return b.list.join(" ");
      // An image contributes its text alternative, so its content stays searchable.
      return `${b.image.alt} ${b.image.caption ?? ""}`;
    })
    .join(" ");

/**
 * An image inside an FAQ answer. Shown inline at the answer's width and linked
 * to the file itself, which is what makes it legible: this one is a dense table
 * at 1404px wide, so it only reads at full resolution -- opening it directly
 * gives the browser's own zoom, including pinch-zoom on a phone. A modal viewer
 * is the wrong tool here; fitting a tall table to the viewport shrinks it.
 */
const AnswerImage = ({ image }: { image: FaqImage }) => (
  <figure className="space-y-2">
    <a
      href={image.src}
      target="_blank"
      rel="noopener noreferrer"
      className="block overflow-hidden rounded-lg border border-border transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <img
        src={image.src}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading="lazy"
        className="h-auto w-full"
      />
    </a>
    <figcaption className="text-xs">
      {image.caption} Select the image to open it full size.
    </figcaption>
  </figure>
);

const AnswerBlocks = ({ blocks }: { blocks: FaqBlock[] }) => (
  <div className="space-y-3 pb-1 text-sm leading-relaxed text-muted-foreground">
    {blocks.map((block, i) => {
      if (typeof block === "string") return <p key={i}>{block}</p>;

      if ("list" in block)
        return (
          <ul key={i} className="list-disc space-y-1 pl-5">
            {block.list.map((li, j) => (
              <li key={j}>{li}</li>
            ))}
          </ul>
        );

      return <AnswerImage key={i} image={block.image} />;
    })}
  </div>
);

const Faq = () => {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const categories = FAQ_CATEGORIES.map((cat) => ({
    ...cat,
    items: q
      ? cat.items.filter((it) =>
          `${it.q} ${blockText(it.a)}`.toLowerCase().includes(q)
        )
      : cat.items,
  })).filter((cat) => cat.items.length > 0);

  return (
    <Layout>
      <div className="pt-24 md:pt-28">
        <div className="container-narrow pb-16 md:pb-24">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-8">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>FAQ</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header */}
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground">
              Frequently Asked Questions
            </h1>
            <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
              Everything you need to know about booking, moving in, payments, and
              living at Home 2 Students. Can't find your answer? We're just a
              message away.
            </p>
          </div>

          {/* Search */}
          <div className="relative mt-8 max-w-xl mx-auto">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions..."
              className="pl-10 h-12 rounded-full"
              aria-label="Search frequently asked questions"
            />
          </div>

          {/* Categories */}
          {categories.length > 0 ? (
            <div className="mt-12 space-y-10">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <section key={cat.id} id={cat.id} className="scroll-mt-28">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary">
                        <Icon className="h-5 w-5" />
                      </span>
                      <h2 className="text-xl sm:text-2xl font-heading font-semibold text-foreground">
                        {cat.title}
                      </h2>
                    </div>
                    <div className="rounded-2xl border bg-card px-4 sm:px-6 shadow-sm">
                      <Accordion type="single" collapsible className="w-full">
                        {cat.items.map((item, idx) => (
                          <AccordionItem
                            key={idx}
                            value={`${cat.id}-${idx}`}
                            className="last:border-b-0"
                          >
                            <AccordionTrigger className="text-left text-base font-medium text-foreground hover:no-underline">
                              {item.q}
                            </AccordionTrigger>
                            <AccordionContent>
                              <AnswerBlocks blocks={item.a} />
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="mt-12 text-center">
              <p className="text-muted-foreground">
                No questions match "{query}". Try a different search, or reach out
                and we'll help you directly.
              </p>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-heading font-semibold text-foreground">
              Still have questions?
            </h2>
            <p className="mt-3 text-muted-foreground">
              Our team is happy to help with anything we haven't covered here.
            </p>
            <Button asChild variant="coral" size="lg" className="mt-6">
              <Link to="/contact">Contact us</Link>
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Faq;
